import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DarkModeToggle from "../../../components/DarkModeToggle";
import { CONTACT_EMAIL, GITHUB_URL } from "../../../config/constants";
import { allProjects } from "../../Portfolio/allProjects";

interface DisplayCard {
  id: string;
  name: string;
  tag: string;
  description: string;
  thumbnail: string;
  link: string;
  isExternal: boolean;
  live: boolean;
}



const Hero: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const phoneSrc = isDarkMode
    ? "/images/undraw_smartphone_dark.svg"
    : "/images/undraw_smartphone_9zbj.svg";

  const cards: DisplayCard[] = [
    {
      id: "strong-habit",
      name: "Refine: Minimal Habit Tracker",
      tag: "iOS · React Native",
      description: allProjects[0].description,
      thumbnail: allProjects[0].thumbnail,
      link: allProjects[0].link,
      isExternal: false,
      live: true,
    },
    {
      id: "kortekai-studio",
      name: "KorTekAI",
      tag: "Web · NextJS",
      description:
        "App Development Studio speacilizing in Ai automation for Mobile apps.",
      thumbnail: "/images/Macbook-air-(2022).png",
      link: "https://kortekai.com",
      isExternal: true,
      live: true,
    },
    {
      id: "coming-soon-1",
      name: "Coming Soon",
      tag: "",
      description: "Next project in progress.",
      thumbnail: "",
      link: "",
      isExternal: false,
      live: false,
    },
    {
      id: "coming-soon-2",
      name: "Coming Soon",
      tag: "",
      description: "Another project on the horizon.",
      thumbnail: "",
      link: "",
      isExternal: false,
      live: false,
    },
  ];

  return (
    <div
      className="min-h-screen w-full font-sans"
      style={{ background: "var(--warm-bg)", color: "var(--warm-text)" }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-10">
      {/* ── Editorial Hero ── */}
      <section className="pt-10 sm:pt-14 lg:pt-18 pb-6 sm:pb-8">
        {/* Name + phone: side-by-side on sm+, stacked on mobile */}
        <div className="flex flex-col-reverse items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
          <h1
            className="font-display"
            style={{
              fontSize: "clamp(1.75rem, 5.5vw, 3.25rem)",
              lineHeight: 1.1,
              letterSpacing: "0.01em",
            }}
          >
            <span className="uppercase">Sky Stanoyevitch.</span>
            <br />
            <span className="uppercase" style={{ fontWeight: 300 }}>Mobile App Developer based<br />in San Diego, CA</span>
          </h1>
          {/* Phone illustrations — same small size at all breakpoints */}
          <div className="relative flex-shrink-0 self-start" style={{ width: "clamp(34px, 4.8vw, 63px)", height: "clamp(70px, 10vw, 130px)", overflow: "visible" }}>
            <img src={phoneSrc} alt="" aria-hidden="true" className="absolute pointer-events-none select-none"
              style={{ height: "clamp(70px, 10vw, 130px)", top: 0, left: 0, transform: "rotate(12deg) translate(8px, 4px)", filter: "drop-shadow(1px 6px 10px rgba(0,0,0,0.12))", zIndex: 0 }} />
            <img src={phoneSrc} alt="" aria-hidden="true" className="absolute pointer-events-none select-none"
              style={{ height: "clamp(70px, 10vw, 130px)", top: 0, left: 0, transform: "rotate(-10deg)", filter: "drop-shadow(1px 6px 10px rgba(0,0,0,0.18))", zIndex: 1 }} />
            <div className="absolute" style={{ top: "16px", left: "6px", transform: "rotate(-10deg) scale(1.4)", transformOrigin: "top left", zIndex: 2 }}>
              <DarkModeToggle variant="inline" />
            </div>
          </div>
        </div>

        {/* Row 2: socials + toggle */}
        <div className="mt-3 flex items-center gap-4">

            <a
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              className="transition-opacity duration-200 hover:opacity-40"
              style={{ color: "var(--warm-muted)" }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              aria-label="Email"
              className="transition-opacity duration-200 hover:opacity-40"
              style={{ color: "var(--warm-muted)" }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </a>
        </div>

      </section>

      {/* ── Marquee ticker ── */}
      <div style={{ borderTop: "1px solid var(--warm-border)", borderBottom: "1px solid var(--warm-border)", overflow: "hidden", width: "100vw", position: "relative", left: "50%", marginLeft: "-50vw" }}>
        <div
          className="flex items-center py-3"
          style={{
            display: "flex",
            width: "max-content",
            animation: "marquee 22s linear infinite",
          }}
        >
          {[...Array(4)].flatMap(() => [
            "Expo", "React Native", "Swift", "Development", "UI/UX Design", "Cross-Platform",
          ]).map((word, i) => (
            <span
              key={i}
              className="font-display uppercase text-xs tracking-widest whitespace-nowrap"
              style={{
                color: "var(--warm-text)",
                padding: "0 2rem",
              }}
            >
              {word}
            </span>
          ))}
        </div>
      </div>

      {/* ── Selected Work ── */}
      <section className="pt-8 sm:pt-10 pb-16 sm:pb-20">
        {/* Section label */}
        <div
          className="flex items-center justify-between mb-4 text-xs font-normal tracking-widest uppercase"
          style={{ color: "var(--warm-muted)" }}
        >
          <span>Work &amp; Projects</span>
        </div>

        {/* Grid: 1 col mobile → 2 col tablet → 4 col desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card) => {
            const isClickable = card.live && !!card.link;
            const sharedClass = `group flex flex-col no-underline transition-colors duration-200`;

            const cardContent = (
              <>
                {/* Thumbnail */}
                <div className="w-full p-3 sm:p-4">
                <div className="w-full overflow-hidden flex items-center justify-center rounded-sm h-28 sm:h-32 lg:h-36">
                    {card.thumbnail ? (
                      <img
                        src={card.thumbnail}
                        alt={card.name}
                        className={`w-full h-full object-cover transition-transform duration-500 ${isClickable ? "group-hover:scale-105" : ""}`}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex flex-col items-center justify-center gap-2"
                        style={{ background: "var(--warm-surface)" }}
                      >
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: "var(--warm-muted)" }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span className="text-xs tracking-widest uppercase" style={{ color: "var(--warm-muted)" }}>Coming Soon</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card body */}
                <div className="flex flex-col flex-1 p-3 sm:p-4">
                  <h3
                    className="font-display leading-tight mb-2 text-base sm:text-lg"
                    style={{ color: card.live ? "var(--warm-text)" : "var(--warm-muted)" }}
                  >
                    {card.name}
                  </h3>
                  <p className="text-xs leading-relaxed line-clamp-2" style={{ color: "var(--warm-muted)" }}>
                    {card.description}
                  </p>
                  {isClickable && (
                    <div className="mt-auto pt-4">
                      <span
                        className="text-xs font-semibold tracking-widest uppercase transition-opacity duration-200 group-hover:opacity-60"
                        style={{ color: "var(--warm-accent)" }}
                      >
                        View ↗
                      </span>
                    </div>
                  )}
                </div>
              </>
            );

            if (card.isExternal && isClickable) {
              return (
                <a key={card.id} href={card.link} target="_blank" rel="noopener noreferrer" className={sharedClass} data-portfolio="true">
                  {cardContent}
                </a>
              );
            }
            if (!card.isExternal && isClickable) {
              return (
                <Link key={card.id} to={card.link} className={sharedClass} data-portfolio="true">
                  {cardContent}
                </Link>
              );
            }
            return <div key={card.id} className={sharedClass}>{cardContent}</div>;
          })}
        </div>

      </section>

      {/* ── Full-bleed divider ── */}
      <div style={{ height: "1px", background: "var(--warm-border)", width: "100vw", position: "relative", left: "50%", marginLeft: "-50vw" }} />

      </div>
    </div>
  );
};

export default Hero;


